import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Shield, Users, Search, Lock, Unlock, Ban, Globe,
  AlertTriangle, ShieldAlert, ShieldCheck, Loader2, RefreshCw,
  User, Mail, Clock, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import IPSecurityManager from '../components/admin/IPSecurityManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportDashboardPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const isSupport = user?.role === 'support' || user?.role === 'admin';

  useEffect(() => {
    if (!authLoading && !isSupport) {
      navigate('/inbox');
    }
    if (isSupport) loadStats();
  }, [authLoading, isSupport]);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/support/dashboard-stats`);
      setStats(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/support/user-lookup`, { params: { search: searchQuery } });
      setSearchResults(res.data.users);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleUnlockAccount = async (email) => {
    try {
      await axios.delete(`${API}/admin/ip-security/unlock-account/${email}`);
      toast.success(`${email} unlocked`);
      handleSearch();
    } catch (err) {
      toast.error('Failed to unlock');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#16162a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  if (!isSupport) return null;

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(); } catch { return d; }
  };

  return (
    <div className="min-h-screen bg-[#16162a]">
      {/* Header */}
      <header className="bg-[#1a1a2e] border-b border-[#2d2d44] px-6 py-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inbox')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">Support Dashboard</h1>
              <p className="text-sm text-gray-400">User support & security management</p>
            </div>
          </div>
          <Badge className="ml-auto bg-emerald-500/20 text-emerald-400">{user?.role}</Badge>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-[#6D4AFF] mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-4 text-center">
                <ShieldAlert className="w-6 h-6 text-red-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats.blocked_ips}</p>
                <p className="text-xs text-gray-500">Blocked IPs</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-4 text-center">
                <Lock className="w-6 h-6 text-amber-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats.locked_accounts}</p>
                <p className="text-xs text-gray-500">Locked Accounts</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-4 text-center">
                <Activity className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats.active_users}</p>
                <p className="text-xs text-gray-500">Active Subscribers</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-[#1a1a2e] border border-[#2d2d44]">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Search className="w-4 h-4 mr-2" /> User Lookup
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" /> IP Security
            </TabsTrigger>
          </TabsList>

          {/* User Lookup Tab */}
          <TabsContent value="users">
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm">Search Users</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by email or name..."
                    className="bg-[#252540] border-[#2d2d44] text-white"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} disabled={searching} className="bg-[#6D4AFF]">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#6D4AFF]/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-[#6D4AFF]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{u.display_name || u.email}</p>
                              <Badge className={`text-xs ${
                                u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                                u.role === 'support' ? 'bg-emerald-500/20 text-emerald-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>{u.role}</Badge>
                              {u.is_locked && <Badge className="bg-red-500/20 text-red-400 text-xs">Locked</Badge>}
                            </div>
                            <p className="text-xs text-gray-500">{u.email}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                              <span>{u.subscription_status} {u.subscription_plan_name && `· ${u.subscription_plan_name}`}</span>
                              <span>Joined {formatDate(u.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {u.is_locked && (
                            <Button onClick={() => handleUnlockAccount(u.email)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                              <Unlock className="w-3 h-3 mr-1" /> Unlock
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IP Security Tab */}
          <TabsContent value="security">
            <IPSecurityManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
